/**
 * FirmWorkpaper — /w/:slug/app/engagements/:engagementId/firm
 *
 * V98-6 / ADR-0047: the firm-only auditor tier. NOT under WorkspaceGate — a
 * firm member holds no client membership (R2). Every call goes to
 * /api/w/:slug/engagements/:id/firm/*, which 404s for anyone not on this
 * engagement's roster (workspace owners included).
 *
 * The shell (Track 6 review):
 *   T6-S1  `?step_up=1` → stamp the fresh sign-in once (`/session/enter`);
 *   T6-B4  a stale step-up mid-session shows one prompt, parks drafts and the
 *          interrupted write, and replays it on return;
 *   T6-H2  one persistent polite status region for every write;
 *   T6-H3  a read-only line on every tab in assembly / custody, which locked
 *          controls point at;
 *   T6-H10 a slim header: breadcrumb back to "Your engagements", the title
 *          "{client} · {program}", the firm name with a "Firm only" chip;
 *   Medium tabs are links with aria-current (state lives in the URL), scroll
 *          sideways on phones, carry counts; a one-line role legend.
 * Each tab lives in pages/firm/*.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, EmptyState, PageHeader, Spinner } from '@aigovops/design-system';
import { useAnnounce } from '../components/today/shared';
import { firmApi, programLabel, type FirmOverview } from '../lib/firm-api';
import { fmtDate, fmtRange } from '../lib/format';
import { STEP_UP_PARAM, startStepUp, stepUpUrlOf } from '../lib/firm-step-up';
import { Chip, FirmProvider, ROLE_LABEL, isLead, takePending, useStepUpGate } from './firm/firm-kit';
import { RosterTab } from './firm/RosterTab';
import { PbcTab } from './firm/PbcTab';
import { PopulationsTab } from './firm/PopulationsTab';
import { TestsTab } from './firm/TestsTab';
import { ExceptionsTab } from './firm/ExceptionsTab';
import { ReportTab } from './firm/ReportTab';
import { ExportTab } from './firm/ExportTab';
import './firm.css';
import './today.css';

export { ReconcileForm } from './firm/PopulationsTab';

const TABS = [
  ['roster', 'Roster'], ['pbc', 'PBC'], ['populations', 'Populations'], ['tests', 'Tests'],
  ['exceptions', 'Exceptions'], ['report', 'Report'], ['export', 'Export'],
] as const;
type Tab = (typeof TABS)[number][0];
const HEADING: Record<Tab, string> = { roster: 'roster-h', pbc: 'pbc-h', populations: 'pop-h', tests: 'tests-h', exceptions: 'exc-h', report: 'report-h', export: 'export-h' };

/**
 * T6-S1 / T6-B4: back from the step-up sign-in (`?step_up=1`) — stamp once via
 * `/session/enter` (the server checks the sign-in is ≤ 10 minutes old), replay
 * the write that was interrupted, drop the flag, then load.
 */
function useStepUpReturn(slug: string, eng: string, announce: (m: string) => void): { done: boolean; error: unknown } {
  const [params, setParams] = useSearchParams();
  const returning = params.get(STEP_UP_PARAM) === '1';
  const [state, setState] = useState<{ done: boolean; error: unknown }>({ done: !returning, error: null });
  useEffect(() => {
    if (!returning) return;
    let live = true;
    (async () => {
      try {
        await firmApi.stepUp(slug, eng);
        const pending = takePending(eng);
        if (pending) {
          try {
            await (firmApi[pending.method] as (...a: unknown[]) => Promise<unknown>)(...pending.args);
            announce(`Signed in again · ${pending.message}`);
          } catch {
            announce('Signed in again · your draft is restored; try that step once more');
          }
        } else announce('Signed in again');
        if (live) setState({ done: true, error: null });
      } catch (e) {
        if (live) setState({ done: true, error: e });
      } finally {
        setParams((p) => { p.delete(STEP_UP_PARAM); return p; }, { replace: true });
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returning, slug, eng]);
  return state;
}

function useOverview(slug: string, eng: string, ready: boolean) {
  const [s, setS] = useState<{ data: FirmOverview | null; raw: unknown; loading: boolean }>({ data: null, raw: null, loading: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!ready) return;
    let live = true;
    firmApi.overview(slug, eng).then((data) => live && setS({ data, raw: null, loading: false }), (raw) => live && setS({ data: null, raw, loading: false }));
    return () => { live = false; };
  }, [slug, eng, ready, n]);
  return { ...s, reload: () => setN((x) => x + 1) };
}

/** Counts on the tab labels ("Tests · 2 to review"). Best-effort. */
function useTabCounts(slug: string, eng: string, o: FirmOverview | null): Partial<Record<Tab, number>> {
  const [c, setC] = useState<Partial<Record<Tab, number>>>({});
  useEffect(() => {
    if (!o) return;
    let live = true;
    void Promise.allSettled([firmApi.pbc(slug, eng), o.me.firm_role === 'staff' ? Promise.resolve(null) : firmApi.reviewQueue(slug, eng)]).then(([p, q]) => {
      if (!live) return;
      setC({
        pbc: p.status === 'fulfilled' ? p.value.requests.filter((r) => r.status === 'submitted').length : 0,
        tests: q.status === 'fulfilled' && q.value ? q.value.awaiting_review.length + q.value.notes_to_clear.length : 0,
      });
    });
    return () => { live = false; };
  }, [slug, eng, o]);
  return c;
}

export function FirmWorkpaper() {
  const { slug = '', engagementId = '' } = useParams();
  const [params] = useSearchParams();
  const tab = (TABS.find(([k]) => k === params.get('tab'))?.[0] ?? 'roster') as Tab;
  const [announcer, announce] = useAnnounce();
  const back = useStepUpReturn(slug, engagementId, announce);
  const ov = useOverview(slug, engagementId, back.done);
  const gate = useStepUpGate(engagementId);
  const counts = useTabCounts(slug, engagementId, ov.data);
  const userTab = useRef(false);
  const tabsRef = useRef<HTMLElement>(null);

  // Keep the active tab in view on a narrow, scrolling tab bar; focus the new tab's heading after a click (T6-H1).
  useEffect(() => {
    tabsRef.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    if (!userTab.current) return;
    userTab.current = false;
    const t = setTimeout(() => document.getElementById(HEADING[tab])?.focus(), 0);
    return () => clearTimeout(t);
  }, [tab]);

  const o = ov.data;
  const ctx = useMemo(() => (o ? {
    slug, eng: engagementId, o, lead: isLead(o.me.firm_role), writable: o.phase === 'fieldwork', announce,
    requireStepUp: gate.requireStepUp, drafts: gate.drafts, lockId: o.phase === 'fieldwork' ? null : 'firm-readonly',
  } : null), [o, slug, engagementId, announce, gate.requireStepUp, gate.drafts]);

  if (!back.done || (ov.loading && !o)) return <div className="gp-firm-page"><main className="gp-firm"><Spinner label="Loading the engagement" />{announcer}</main></div>;
  if (!o || !ctx) {
    const stepUp = stepUpUrlOf(back.error) ?? stepUpUrlOf(ov.raw);
    return (
      <div className="gp-firm-page">
        <main className="gp-firm">
          <FirmCrumbs current={null} />
          {stepUp ? (
            <EmptyState title="Sign in again to open the working papers" description="Firm working papers need a fresh sign-in with MFA."
              action={<Button variant="primary" onClick={() => startStepUp(stepUp)}>Sign in again</Button>} />
          ) : (
            <EmptyState title="Engagement not found" description="It doesn’t exist, or you’re not on its roster."
              action={<Link to="/audit-firm">Your engagements</Link>} />
          )}
          {announcer}
        </main>
      </div>
    );
  }
  const program = programLabel(o.period);
  const title = `${o.client.name} · ${program ?? (o.period ? fmtRange(o.period.start, o.period.end) : 'Audit')}`;
  const phaseLine: ReactNode = o.phase === 'assembly'
    ? <>Assembly · read-only until {fmtDate(o.window.assembly_deadline)} · sign-offs and review notes still open</>
    : o.phase === 'custody'
      ? <>Custody · read and export only until {fmtDate(o.window.retention_end)}</>
      : null;
  return (
    <FirmProvider value={ctx}>
      <div className="gp-firm-page">
        <main className="gp-firm">
          <FirmCrumbs current={title} />
          <PageHeader title={title} subtitle={<span className="gp-firm-actions">{o.firm.name} <Chip>Firm only</Chip></span>} />
          <p className="gp-firm-meta">
            {o.period ? <span>Period {fmtRange(o.period.start, o.period.end)}</span> : null}
            <span>Report {fmtDate(o.engagement.report_date ?? o.window.report_date)}</span>
            <span>You: {ROLE_LABEL[o.me.firm_role]}</span>
          </p>
          <p className="gp-firm-legend">Partner and manager: roster, issue, report · Senior and up: review · Everyone: prepare</p>
          {phaseLine ? <p className="gp-firm-readonly" id="firm-readonly">{phaseLine}</p> : null}
          {o.engagement.revoked_at && o.phase !== 'custody' ? <p className="gp-firm-readonly">The client ended access {fmtDate(o.engagement.revoked_at)}</p> : null}
          {gate.need ? (
            <Alert variant="warning" title="Sign in again to keep working">
              <span className="gp-firm-actions">Your sign-in for the working papers expired. What you typed is kept. <Button variant="primary" compact onClick={gate.go}>Sign in again</Button></span>
            </Alert>
          ) : null}
          <nav className="gp-firm-tabs" aria-label="Working papers" ref={tabsRef}>
            {TABS.map(([k, label]) => (
              <Link key={k} to={`?tab=${k}`} replace aria-current={tab === k ? 'page' : undefined} className="gp-firm-tab"
                onClick={() => { userTab.current = true; }}>
                {label}{counts[k] ? <span className="gp-firm-count"> · {counts[k]} to review</span> : null}
              </Link>
            ))}
          </nav>
          <section className="gp-firm-panel" aria-labelledby={HEADING[tab]}>
            {tab === 'roster' && <RosterTab />}
            {tab === 'pbc' && <PbcTab />}
            {tab === 'populations' && <PopulationsTab />}
            {tab === 'tests' && <TestsTab />}
            {tab === 'exceptions' && <ExceptionsTab />}
            {tab === 'report' && <ReportTab />}
            {tab === 'export' && <ExportTab />}
          </section>
          {announcer}
        </main>
      </div>
    </FirmProvider>
  );
}

/** T6-H10: the slim firm header's way back. */
export function FirmCrumbs({ current }: { current: string | null }) {
  return (
    <nav className="gp-firm-crumbs" aria-label="Breadcrumb">
      <ol>
        <li><Link to="/audit-firm">Your engagements</Link></li>
        {current ? <li aria-current="page">{current}</li> : null}
      </ol>
    </nav>
  );
}
